/**
 * Queries over a built graph (graphModel.buildGraph) — pure, shared by the graph
 * page and the MCP graph tools. Every answer is capped: a model asking for
 * "everything around a workspace" must get a bounded, truncation-flagged reply,
 * never a 20k-node dump.
 */

export const DEFAULT_NODE_CAP = 200;

/** Adjacency index, built once per graph and reused by every query. */
export function indexGraph(graph) {
  const byId = new Map();
  const adj = new Map();
  for (const n of graph?.nodes || []) {
    byId.set(n.id, n);
    adj.set(n.id, []);
  }
  for (const e of graph?.edges || []) {
    if (!adj.has(e.from) || !adj.has(e.to)) continue;
    adj.get(e.from).push({ edge: e, other: e.to });
    adj.get(e.to).push({ edge: e, other: e.from });
  }
  return { graph, byId, adj };
}

const matches = (list, value) => !Array.isArray(list) || list.length === 0 || list.includes(value);

/**
 * A scope filter keeps nodes whose wire `scope` is listed. Nodes with no scope
 * (collections, folders, requests, environments — workspace-scoped) never
 * match a scope filter: they belong to no personal or team scope.
 */
const inScopes = (scopes, node) => !Array.isArray(scopes) || scopes.length === 0 || scopes.includes(node?.scope);

/** The part of a graph in one wire scope (`personal` | `team:<id>`); null/'' = all. */
export function filterByScope(nodes, edges, scope) {
  if (!scope) return { nodes, edges };
  const kept = nodes.filter((n) => n.scope === scope);
  const keep = new Set(kept.map((n) => n.id));
  return { nodes: kept, edges: edges.filter((e) => keep.has(e.from) && keep.has(e.to)) };
}

/**
 * Everything within `depth` hops of `id` (both directions), breadth-first so
 * the nearest items survive the cap.
 */
export function neighbors(index, id, { depth = 1, relations, types, origins, scopes, cap = DEFAULT_NODE_CAP } = {}) {
  if (!index.byId.has(id)) return null;
  const maxDepth = Math.max(1, Math.min(3, Number(depth) || 1));
  const seen = new Map([[id, 0]]);
  const edges = new Map();
  let frontier = [id];
  let truncated = false;

  for (let d = 1; d <= maxDepth && frontier.length; d += 1) {
    const next = [];
    for (const current of frontier) {
      for (const { edge, other } of index.adj.get(current)) {
        if (!matches(relations, edge.rel) || !matches(origins, edge.origin)) continue;
        const node = index.byId.get(other);
        if (!seen.has(other)) {
          // The centre is exempt: it is in `seen` before the walk starts.
          if (!matches(types, node.type) || !inScopes(scopes, node)) continue;
          if (seen.size >= cap) {
            truncated = true;
            continue;
          }
          seen.set(other, d);
          next.push(other);
        }
        edges.set(edge.id, edge);
      }
    }
    frontier = next;
  }

  // The outermost ring is never expanded, so links BETWEEN its nodes were not
  // collected above; add every allowed edge whose two ends are in the result.
  for (const nid of seen.keys()) {
    for (const { edge, other } of index.adj.get(nid)) {
      if (seen.has(other) && matches(relations, edge.rel) && matches(origins, edge.origin)) edges.set(edge.id, edge);
    }
  }
  return {
    center: id,
    nodes: [...seen.entries()].map(([nid, distance]) => ({ ...index.byId.get(nid), distance })),
    edges: [...edges.values()],
    truncated,
  };
}

/** Shortest undirected path from → to, as alternating nodes and edges; null if none. */
export function shortestPath(index, from, to, { maxDepth = 6 } = {}) {
  if (!index.byId.has(from) || !index.byId.has(to)) return null;
  if (from === to) return { nodes: [index.byId.get(from)], edges: [] };
  const prev = new Map([[from, null]]);
  let frontier = [from];
  for (let d = 0; d < maxDepth && frontier.length; d += 1) {
    const next = [];
    for (const current of frontier) {
      for (const { edge, other } of index.adj.get(current)) {
        if (prev.has(other)) continue;
        prev.set(other, { node: current, edge });
        if (other === to) {
          const nodes = [];
          const edges = [];
          let step = to;
          while (step !== from) {
            const p = prev.get(step);
            nodes.unshift(index.byId.get(step));
            edges.unshift(p.edge);
            step = p.node;
          }
          nodes.unshift(index.byId.get(from));
          return { nodes, edges };
        }
        next.push(other);
      }
    }
    frontier = next;
  }
  return null;
}

/** Case-insensitive label search; exact and prefix matches first. */
export function searchNodes(index, query, { types, scopes, limit = 25 } = {}) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const scored = [];
  for (const n of index.byId.values()) {
    if (!matches(types, n.type) || !inScopes(scopes, n)) continue;
    const label = String(n.label || '').toLowerCase();
    const at = label.indexOf(q);
    if (at < 0) continue;
    scored.push({ n, score: label === q ? 0 : at === 0 ? 1 : 2, len: label.length });
  }
  scored.sort((a, b) => a.score - b.score || a.len - b.len);
  // Callers cap what they show; allowing one past their cap is how they
  // detect truncation (the MCP handler asks for limit + 1).
  return scored.slice(0, Math.max(1, Math.min(500, limit))).map((s) => s.n);
}

/** A project's members and the links among them (plus 1 hop outward, flagged). */
export function projectSubgraph(index, projectNodeId, { cap = 500 } = {}) {
  if (!index.byId.has(projectNodeId)) return null;
  const members = new Set([projectNodeId]);
  let truncated = false;
  for (const { edge, other } of index.adj.get(projectNodeId)) {
    if (edge.rel !== 'contains' || edge.from !== projectNodeId) continue;
    if (members.size >= cap) {
      truncated = true;
      continue;
    }
    members.add(other);
  }
  const nodes = new Map();
  const edges = new Map();
  for (const m of members) nodes.set(m, { ...index.byId.get(m), member: true });
  for (const m of members) {
    for (const { edge, other } of index.adj.get(m)) {
      if (!nodes.has(other)) {
        if (nodes.size >= cap) {
          truncated = true;
          continue;
        }
        nodes.set(other, { ...index.byId.get(other), member: false });
      }
      edges.set(edge.id, edge);
    }
  }
  return {
    project: index.byId.get(projectNodeId),
    nodes: [...nodes.values()],
    edges: [...edges.values()].filter((e) => nodes.has(e.from) && nodes.has(e.to)),
    truncated,
  };
}

/** Node count per type, for the page's filter chips and an MCP overview. */
export function typeCounts(graph) {
  const counts = {};
  for (const n of graph?.nodes || []) counts[n.type] = (counts[n.type] || 0) + 1;
  return counts;
}
